/**
 * Smart Run Planner - Plan safe, healthy running routes
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRunPlannerStore } from '@/stores';
import { useLocationStore } from '@/stores';
import { searchPlaces } from '@/services/api/geocodingApi';
import { parseRouteQuery, summarizePreferences, EXAMPLE_QUERIES } from '@/services/routing/queryParser';
import { DISTANCE_PRESETS } from '@/types/runPlanner';
import { cn } from '@/utils/cn';
import { RouteCard } from './RouteCard';
import { ScheduledRunCard } from './ScheduledRunCard';

export function RunPlanner() {
  const {
    isOpen,
    activeTab,
    isGenerating,
    startPoint,
    endPoint,
    targetDistance,
    preferences,
    scheduledTime,
    generatedRoutes,
    selectedRoute,
    scheduledRuns,
    closePlanner,
    setActiveTab,
    setStartPoint,
    setEndPoint,
    setTargetDistance,
    setPreferences,
    setScheduledTime,
    generateRoutes,
    selectRoute,
    scheduleRun,
  } = useRunPlannerStore();

  const currentPosition = useLocationStore((state) => state.currentPosition);
  const [customDistance, setCustomDistance] = useState('');
  const [endAddress, setEndAddress] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [naturalQuery, setNaturalQuery] = useState('');
  const [parsedPreferences, setParsedPreferences] = useState<string[]>([]);

  const handleDestinationSearch = async () => {
    if (!endAddress.trim() || !currentPosition) return;

    setIsSearching(true);
    try {
      const results = await searchPlaces(endAddress, {
        limit: 5,
        userLocation: { lat: currentPosition.lat, lng: currentPosition.lng },
      });
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching for destination:', error);
    } finally {
      setIsSearching(false);
    }
  };

  // Always update start point to current GPS location when planner is open
  useEffect(() => {
    if (currentPosition && isOpen) {
      console.log('RunPlanner: Updating start point to current GPS location:', currentPosition);
      setStartPoint({
        lat: currentPosition.lat,
        lng: currentPosition.lng,
        address: 'Current Location',
      });
    }
  }, [currentPosition, isOpen, setStartPoint]);

  if (!isOpen) return null;

  const distanceKm = targetDistance / 1000;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        onClick={(e) => e.target === e.currentTarget && closePlanner()}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="relative w-full max-w-2xl max-h-[90vh] bg-[#0a1520] border border-white/10 rounded-2xl overflow-hidden shadow-2xl"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                <RunIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Smart Run Planner</h2>
                <p className="text-xs text-gray-400">Plan safe, healthy routes</p>
              </div>
            </div>
            <button
              onClick={closePlanner}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              <CloseIcon className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-white/10">
            {(['plan', 'routes', 'scheduled'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={cn(
                  'flex-1 px-4 py-3 text-sm font-medium transition-colors relative',
                  activeTab === tab
                    ? 'text-cyan-400'
                    : 'text-gray-400 hover:text-white'
                )}
              >
                {tab === 'plan' && 'Plan Run'}
                {tab === 'routes' && `Routes ${generatedRoutes.length > 0 ? `(${generatedRoutes.length})` : ''}`}
                {tab === 'scheduled' && `Scheduled ${scheduledRuns.length > 0 ? `(${scheduledRuns.length})` : ''}`}
                {activeTab === tab && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-cyan-400"
                  />
                )}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="p-4 overflow-y-auto max-h-[calc(90vh-140px)]">
            {/* Plan Tab */}
            {activeTab === 'plan' && (
              <div className="space-y-6">
                {/* Starting Point */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Starting Point
                  </label>
                  <div className="flex flex-col gap-1 p-3 bg-white/5 border border-white/10 rounded-xl">
                    <div className="flex items-center gap-2">
                      <LocationIcon className="w-5 h-5 text-cyan-400" />
                      <span className="text-white">
                        {currentPosition ? 'Current GPS Location' : 'Loading GPS...'}
                      </span>
                    </div>
                    {currentPosition && (
                      <div className="text-xs text-gray-400 ml-7">
                        📍 {currentPosition.lat.toFixed(4)}, {currentPosition.lng.toFixed(4)}
                      </div>
                    )}
                  </div>
                </div>

                {/* Route Type Toggle */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Route Type
                  </label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setPreferences({ loopRoute: true });
                        setEndPoint(null);
                        setEndAddress('');
                      }}
                      className={cn(
                        'flex-1 p-3 rounded-xl border transition-all text-center',
                        preferences.loopRoute
                          ? 'bg-cyan-500/20 border-cyan-500 text-cyan-400'
                          : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/20'
                      )}
                    >
                      <div className="text-sm font-medium">Loop Route</div>
                      <div className="text-[10px] opacity-70">Return to start</div>
                    </button>
                    <button
                      onClick={() => setPreferences({ loopRoute: false })}
                      className={cn(
                        'flex-1 p-3 rounded-xl border transition-all text-center',
                        !preferences.loopRoute
                          ? 'bg-cyan-500/20 border-cyan-500 text-cyan-400'
                          : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/20'
                      )}
                    >
                      <div className="text-sm font-medium">Point to Point</div>
                      <div className="text-[10px] opacity-70">Different end</div>
                    </button>
                  </div>
                </div>

                {/* End Point - Only shown for point-to-point */}
                {!preferences.loopRoute && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Destination
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        placeholder="Search for destination address..."
                        value={endAddress}
                        onChange={(e) => {
                          setEndAddress(e.target.value);
                          if (e.target.value.length === 0) {
                            setSearchResults([]);
                          }
                        }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && endAddress.trim()) {
                            handleDestinationSearch();
                          }
                        }}
                        className="flex-1 px-3 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500"
                      />
                      <button
                        onClick={handleDestinationSearch}
                        disabled={isSearching || !endAddress.trim()}
                        className="px-4 py-3 bg-cyan-500/20 border border-cyan-500 text-cyan-400 rounded-xl hover:bg-cyan-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSearching ? 'Searching...' : 'Search'}
                      </button>
                    </div>

                    {/* Search Results */}
                    {searchResults.length > 0 && !endPoint && (
                      <div className="mt-2 space-y-1 max-h-48 overflow-y-auto bg-white/5 border border-white/10 rounded-xl p-2">
                        {searchResults.map((result) => (
                          <button
                            key={result.id}
                            onClick={() => {
                              setEndPoint({
                                lat: result.coordinates.lat,
                                lng: result.coordinates.lng,
                                address: result.name,
                              });
                              setSearchResults([]);
                              setEndAddress(result.name);
                            }}
                            className="w-full text-left p-2 hover:bg-white/10 rounded-lg transition-colors"
                          >
                            <div className="text-sm text-white">{result.name}</div>
                            <div className="text-xs text-gray-400">{result.address}</div>
                          </button>
                        ))}
                      </div>
                    )}

                    {endPoint && (
                      <div className="mt-2 flex items-center gap-2 p-3 bg-white/5 border border-white/10 rounded-xl">
                        <LocationIcon className="w-5 h-5 text-emerald-400" />
                        <span className="text-white text-sm">
                          {endPoint.address || `${endPoint.lat.toFixed(4)}, ${endPoint.lng.toFixed(4)}`}
                        </span>
                        <button
                          onClick={() => {
                            setEndPoint(null);
                            setEndAddress('');
                          }}
                          className="ml-auto text-xs text-red-400 hover:underline"
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Distance Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Target Distance
                  </label>
                  <div className="grid grid-cols-5 gap-2 mb-3">
                    {DISTANCE_PRESETS.map((preset) => (
                      <button
                        key={preset.value}
                        onClick={() => setTargetDistance(preset.value)}
                        className={cn(
                          'p-3 rounded-xl border text-center transition-all',
                          targetDistance === preset.value
                            ? 'bg-cyan-500/20 border-cyan-500 text-cyan-400'
                            : 'bg-white/5 border-white/10 text-gray-400 hover:border-white/20'
                        )}
                      >
                        <div className="text-lg font-bold">{preset.label}</div>
                        <div className="text-[10px] opacity-70">{preset.description}</div>
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      placeholder="Custom (km)"
                      value={customDistance}
                      onChange={(e) => {
                        setCustomDistance(e.target.value);
                        if (e.target.value) {
                          setTargetDistance(parseFloat(e.target.value) * 1000);
                        }
                      }}
                      className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500"
                    />
                    <span className="text-gray-400">km</span>
                  </div>
                </div>

                {/* Natural Language Query - ChatGPT-style */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <span className="flex items-center gap-2">
                      🤖 Describe Your Ideal Route
                      <span className="text-xs text-gray-500">(AI-powered)</span>
                    </span>
                  </label>
                  <div className="space-y-3">
                    {/* Query Input */}
                    <div className="relative">
                      <textarea
                        value={naturalQuery}
                        onChange={(e) => {
                          setNaturalQuery(e.target.value);
                          if (e.target.value.trim()) {
                            const prefs = parseRouteQuery(e.target.value);
                            setParsedPreferences(summarizePreferences(prefs).split(', '));
                          } else {
                            setParsedPreferences([]);
                          }
                        }}
                        placeholder="e.g., More shade, good air quality, pass by cafes and parks..."
                        rows={2}
                        className={cn(
                          'w-full px-4 py-3 bg-gradient-to-br from-purple-500/10 to-cyan-500/10',
                          'border-2 border-purple-500/20 rounded-xl text-white placeholder-gray-500',
                          'focus:outline-none focus:border-purple-500/40 focus:ring-2 focus:ring-purple-500/20',
                          'resize-none transition-all'
                        )}
                      />
                      {naturalQuery && (
                        <button
                          onClick={() => {
                            setNaturalQuery('');
                            setParsedPreferences([]);
                          }}
                          className="absolute top-2 right-2 p-1 rounded-lg hover:bg-white/10 transition-colors"
                        >
                          <CloseIcon className="w-4 h-4 text-gray-400" />
                        </button>
                      )}
                    </div>

                    {/* Example Queries */}
                    {!naturalQuery && (
                      <div className="flex flex-wrap gap-2">
                        <span className="text-xs text-gray-500">Try:</span>
                        {EXAMPLE_QUERIES.slice(0, 2).map((example, i) => (
                          <button
                            key={i}
                            onClick={() => {
                              setNaturalQuery(example);
                              const prefs = parseRouteQuery(example);
                              setParsedPreferences(summarizePreferences(prefs).split(', '));
                            }}
                            className="text-xs px-2 py-1 bg-purple-500/10 hover:bg-purple-500/20 text-purple-300 rounded-lg transition-colors"
                          >
                            "{example}"
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Parsed Preferences Display */}
                    {parsedPreferences.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-3 bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border border-purple-500/20 rounded-xl"
                      >
                        <div className="text-xs text-gray-400 mb-2">✨ AI Understood:</div>
                        <div className="flex flex-wrap gap-2">
                          {parsedPreferences.map((pref, i) => (
                            <span
                              key={i}
                              className="px-2 py-1 bg-purple-500/20 text-purple-200 text-xs rounded-lg border border-purple-500/30"
                            >
                              {pref}
                            </span>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </div>
                </div>

                {/* Route Preferences */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-3">
                    <span className="flex items-center gap-2">
                      Route Preferences
                      <span className="text-xs text-gray-500">(Optional: Fine-tune)</span>
                    </span>
                  </label>
                  <div className="space-y-3">
                    {/* Air Quality Priority */}
                    <div className="flex items-center justify-between p-3 bg-white/5 border border-white/10 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                          <AirIcon className="w-4 h-4 text-emerald-400" />
                        </div>
                        <div>
                          <div className="text-sm text-white">Air Quality Priority</div>
                          <div className="text-xs text-gray-400">Avoid pollution hotspots</div>
                        </div>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={preferences.airQuality}
                        onChange={(e) => setPreferences({ airQuality: parseInt(e.target.value) })}
                        className="w-24 accent-emerald-500"
                      />
                    </div>

                    {/* Safety Priority */}
                    <div className="flex items-center justify-between p-3 bg-white/5 border border-white/10 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                          <ShieldIcon className="w-4 h-4 text-blue-400" />
                        </div>
                        <div>
                          <div className="text-sm text-white">Minimize Road Crossings</div>
                          <div className="text-xs text-gray-400">Safer running paths</div>
                        </div>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={preferences.minimalCrossings}
                        onChange={(e) => setPreferences({ minimalCrossings: parseInt(e.target.value) })}
                        className="w-24 accent-blue-500"
                      />
                    </div>

                    {/* Green Spaces */}
                    <div className="flex items-center justify-between p-3 bg-white/5 border border-white/10 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
                          <TreeIcon className="w-4 h-4 text-green-400" />
                        </div>
                        <div>
                          <div className="text-sm text-white">Prefer Green Spaces</div>
                          <div className="text-xs text-gray-400">Parks, trails, nature</div>
                        </div>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={preferences.greenSpaces}
                        onChange={(e) => setPreferences({ greenSpaces: parseInt(e.target.value) })}
                        className="w-24 accent-green-500"
                      />
                    </div>

                    {/* Toggles */}
                    <div className="grid grid-cols-2 gap-2">
                      <ToggleOption
                        label="Loop Route"
                        description="Return to start"
                        checked={preferences.loopRoute}
                        onChange={(v) => setPreferences({ loopRoute: v })}
                      />
                      <ToggleOption
                        label="Water Stops"
                        description="Include fountains"
                        checked={preferences.waterStops}
                        onChange={(v) => setPreferences({ waterStops: v })}
                      />
                      <ToggleOption
                        label="Well-lit Paths"
                        description="For evening runs"
                        checked={preferences.lighting}
                        onChange={(v) => setPreferences({ lighting: v })}
                      />
                      <ToggleOption
                        label="Shaded Route"
                        description="Tree coverage"
                        checked={preferences.shade}
                        onChange={(v) => setPreferences({ shade: v })}
                      />
                    </div>
                  </div>
                </div>

                {/* Schedule Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Schedule (Optional)
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="date"
                      value={scheduledTime?.date || ''}
                      onChange={(e) => setScheduledTime({
                        date: e.target.value,
                        time: scheduledTime?.time || '06:30',
                      })}
                      className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                    />
                    <input
                      type="time"
                      value={scheduledTime?.time || ''}
                      onChange={(e) => setScheduledTime({
                        date: scheduledTime?.date || new Date().toISOString().split('T')[0],
                        time: e.target.value,
                      })}
                      className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                    />
                  </div>
                </div>

                {/* Generate Button */}
                <button
                  onClick={generateRoutes}
                  disabled={isGenerating || !startPoint}
                  className={cn(
                    'w-full py-4 rounded-xl font-semibold text-lg transition-all flex items-center justify-center gap-2',
                    isGenerating
                      ? 'bg-gray-600 text-gray-300 cursor-wait'
                      : 'bg-gradient-to-r from-cyan-500 to-teal-500 text-black hover:shadow-[0_0_30px_rgba(0,255,213,0.3)]'
                  )}
                >
                  {isGenerating ? (
                    <>
                      <LoadingSpinner />
                      Generating Routes...
                    </>
                  ) : (
                    <>
                      <MapIcon className="w-5 h-5" />
                      Generate {distanceKm}K Routes
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Routes Tab */}
            {activeTab === 'routes' && (
              <div className="space-y-4">
                {generatedRoutes.length === 0 ? (
                  <div className="text-center py-12">
                    <MapIcon className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400">No routes generated yet</p>
                    <button
                      onClick={() => setActiveTab('plan')}
                      className="mt-4 px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg hover:bg-cyan-500/30 transition-colors"
                    >
                      Plan a Run
                    </button>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-gray-400 mb-4">
                      {generatedRoutes.length} routes found for your {distanceKm}K run
                    </p>
                    {generatedRoutes.map((route) => (
                      <RouteCard
                        key={route.id}
                        route={route}
                        isSelected={selectedRoute?.id === route.id}
                        onSelect={() => selectRoute(route)}
                        onSchedule={() => scheduleRun(route)}
                      />
                    ))}
                  </>
                )}
              </div>
            )}

            {/* Scheduled Tab */}
            {activeTab === 'scheduled' && (
              <div className="space-y-4">
                {scheduledRuns.length === 0 ? (
                  <div className="text-center py-12">
                    <CalendarIcon className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400">No scheduled runs</p>
                    <button
                      onClick={() => setActiveTab('plan')}
                      className="mt-4 px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg hover:bg-cyan-500/30 transition-colors"
                    >
                      Plan a Run
                    </button>
                  </div>
                ) : (
                  scheduledRuns.map((run) => (
                    <ScheduledRunCard key={run.id} run={run} />
                  ))
                )}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// Toggle Option Component
function ToggleOption({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className={cn(
        'p-3 rounded-xl border text-left transition-all',
        checked
          ? 'bg-cyan-500/20 border-cyan-500'
          : 'bg-white/5 border-white/10 hover:border-white/20'
      )}
    >
      <div className="flex items-center gap-2">
        <div className={cn(
          'w-4 h-4 rounded border-2 flex items-center justify-center transition-colors',
          checked ? 'bg-cyan-500 border-cyan-500' : 'border-gray-500'
        )}>
          {checked && <CheckIcon className="w-3 h-3 text-black" />}
        </div>
        <div>
          <div className="text-sm text-white">{label}</div>
          <div className="text-[10px] text-gray-400">{description}</div>
        </div>
      </div>
    </button>
  );
}

// Loading Spinner
function LoadingSpinner() {
  return (
    <div className="w-5 h-5 border-2 border-gray-400 border-t-white rounded-full animate-spin" />
  );
}

// Icons
function RunIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function LocationIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function AirIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
    </svg>
  );
}

function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );
}

function TreeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  );
}

function MapIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
    </svg>
  );
}

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
    </svg>
  );
}

export default RunPlanner;
